import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconChevronLeft as ChevronLeft } from '@tabler/icons-react';
import { Button, cn } from '@heroui/react';
import { navigateBackWithFallback } from '@/lib/navigation';

export interface NavBarProps {
  /**
   * 标题
   */
  title?: string;
  /**
   * 是否显示返回按钮
   * @default false
   */
  showBack?: boolean;
  /**
   * 返回按钮点击回调（不提供则使用封装的默认返回逻辑）
   */
  onBack?: () => void;
  /**
   * 默认返回兜底路径（当 history 不可回退时生效）
   */
  backFallbackPath?: string;
  /**
   * 右侧操作按钮
   */
  rightActions?: React.ReactNode;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 是否固定在顶部
   * @default true
   */
  fixed?: boolean;
  /**
   * 背景色
   */
  backgroundColor?: string;
  /**
   * 是否显示底部边框
   * @default true
   */
  showBorder?: boolean;
}

/**
 * 移动端顶部导航栏组件
 * 
 * 特性：
 * - ✅ 支持标题显示
 * - ✅ 支持返回按钮
 * - ✅ 支持右侧操作按钮
 * - ✅ 处理安全区域（iOS notch）
 * - ✅ 响应式设计
 */
export function NavBar({
  title,
  showBack = false,
  onBack,
  backFallbackPath,
  rightActions,
  className,
  fixed = true,
  backgroundColor = 'color-mix(in srgb, var(--background) 72%, transparent)',
  showBorder = true,
}: NavBarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigateBackWithFallback(navigate, location, backFallbackPath);
    }
  };

  return (
    <div
      className={cn(
        'w-full bg-background',
        fixed && 'fixed top-0 left-1/2 -translate-x-1/2 z-50',
        showBorder && 'border-b border-border',
        className
      )}
      style={{
        maxWidth: 'var(--app-max-width)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        height: 'calc(var(--navbar-height, 44px) + env(safe-area-inset-top))',
        backgroundColor,
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
      }}
    >
      <div className="flex items-center justify-between h-[var(--navbar-height,44px)] pr-4 pl-2">
        {/* 左侧：返回按钮 */}
        <div className="flex items-center min-w-[60px]">
          {showBack && (
            <Button
              variant="ghost"
              isIconOnly
              onPress={handleBack}
              className="h-8 w-8"
              aria-label="返回"
            >
              <ChevronLeft size={20} stroke={1.5} />
            </Button>
          )}
        </div>

        {/* 中间：标题 */}
        <div className="flex-1 text-center">
          {title && (
            <h1 className="text-base font-semibold text-foreground truncate px-2">
              {title}
            </h1>
          )}
        </div>

        {/* 右侧：操作按钮 */}
        <div className="flex items-center justify-end min-w-[60px]">
          {rightActions}
        </div>
      </div>
    </div>
  );
}
